import { useState, useCallback } from 'react';
import OrganizationService from '../../superAdmin/TenantOrganizations/OrganizationService';
import { UserOrganization } from '../../superAdmin/TenantOrganizations/OrganizationService';
import { useSessionStorage } from './useLocalStorage';
import { useSecureKeyActions } from '../../../stores/secureKeyStore';

/**
 * Hook for managing user organizations and switching between them
 */
export const useOrganization = () => {
    const organizationService = new OrganizationService();
    const [, setTokens] = useSessionStorage('tokens', null);
    const { clearKey } = useSecureKeyActions();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * Fetch all organizations for the current user
     */
    const getMyOrganizations = useCallback(async (): Promise<
        UserOrganization[]
    > => {
        setIsLoading(true);
        setError(null);
        try {
            return await organizationService.getMyOrganizations();
        } catch (err) {
            const errorMessage = 'Failed to fetch organizations';
            setError(errorMessage);
            console.error(errorMessage, err);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    /**
     * Switch to a different organization
     * This will update the JWT token with the new organization
     * Returns the new tokens so the caller can decide what to do
     */
    const switchOrganization = useCallback(
        async (
            organizationId: string
        ): Promise<{
            token: string;
            refreshToken: string;
            refreshTokenExpiryTime: string;
        }> => {
            setIsLoading(true);
            setError(null);
            try {
                // Call the backend to switch organization
                const response =
                    await organizationService.switchOrganization(
                        organizationId
                    );

                // Update tokens in session storage
                setTokens({
                    token: response.token,
                    refreshToken: response.refreshToken,
                    refreshTokenExpiryTime: response.refreshTokenExpiryTime,
                });

                // Clear secure key when switching organization
                // Secure keys are bound to a specific organization for security
                clearKey();
                console.log('Secure key cleared due to organization switch');

                // Return the response so caller can handle the update
                return response;
            } catch (err) {
                const errorMessage = 'Failed to switch organization';
                setError(errorMessage);
                console.error(errorMessage, err);
                throw err;
            } finally {
                setIsLoading(false);
            }
        },
        [setTokens, clearKey]
    );

    return {
        getMyOrganizations,
        switchOrganization,
        isLoading,
        error,
    };
};
